import { useState } from "react";
import { User, KeyRound, Loader2, Wallet } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const FIELD = "w-full bg-transparent border border-aura-border rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-white/40 outline-none transition-colors";
const LABEL = "block text-xs uppercase tracking-[0.1em] text-zinc-500 mb-1.5";

export function CreatorProfile({ creator, onUpdated }) {
  const { t } = useLang();
  const [form, setForm] = useState({
    email: creator.email || "", country: creator.country || "", address: creator.address || "",
    instagram: creator.instagram || "", tiktok: creator.tiktok || "", youtube: creator.youtube || "",
  });
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [pwdSaving, setPwdSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setP = (k) => (e) => setPwd((p) => ({ ...p, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/creator/profile", form);
      onUpdated && onUpdated(data);
      toast.success(t("cdash.saved"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally { setSaving(false); }
  };

  const changePwd = async (e) => {
    e.preventDefault();
    setPwdSaving(true);
    try {
      await api.patch("/creator/password", pwd);
      setPwd({ current_password: "", new_password: "" });
      toast.success(t("cdash.pwdChanged"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally { setPwdSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="creator-profile">
      <form onSubmit={save} className="rounded-2xl border border-aura-border bg-aura-surface p-6 md:p-8">
        <div className="flex items-center gap-2 mb-6"><User size={18} className="text-zinc-400" /><h2 className="font-display text-lg font-semibold text-white">{t("cdash.profileTitle")}</h2></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><label className={LABEL}>{t("cdash.email")}</label><input data-testid="profile-email" type="email" className={FIELD} value={form.email} onChange={set("email")} /></div>
          <div><label className={LABEL}>{t("cdash.country")}</label><input data-testid="profile-country" className={FIELD} value={form.country} onChange={set("country")} /></div>
          <div><label className={LABEL}>{t("cdash.address")}</label><input data-testid="profile-address" className={FIELD} value={form.address} onChange={set("address")} /></div>
          <div><label className={LABEL}>{t("cdash.instagram")}</label><input data-testid="profile-instagram" className={FIELD} value={form.instagram} onChange={set("instagram")} placeholder="@username" /></div>
          <div><label className={LABEL}>{t("cdash.tiktok")}</label><input data-testid="profile-tiktok" className={FIELD} value={form.tiktok} onChange={set("tiktok")} placeholder="@username" /></div>
          <div className="sm:col-span-2"><label className={LABEL}>{t("cdash.youtube")}</label><input data-testid="profile-youtube" className={FIELD} value={form.youtube} onChange={set("youtube")} /></div>
        </div>
        <button data-testid="profile-save-btn" type="submit" disabled={saving} className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-black font-semibold text-sm px-6 py-2.5 hover:bg-zinc-200 active:scale-95 transition-[background-color,transform] duration-300 disabled:opacity-60">
          {saving ? <Loader2 size={16} className="animate-spin" /> : null} {saving ? t("cdash.saving") : t("cdash.save")}
        </button>
      </form>

      <div className="flex flex-col gap-6">
        <form onSubmit={changePwd} className="rounded-2xl border border-aura-border bg-aura-surface p-6 md:p-8">
          <div className="flex items-center gap-2 mb-6"><KeyRound size={18} className="text-zinc-400" /><h2 className="font-display text-lg font-semibold text-white">{t("cdash.pwdTitle")}</h2></div>
          <div className="flex flex-col gap-4">
            <div><label className={LABEL}>{t("cdash.currentPwd")}</label><input data-testid="pwd-current" type="password" className={FIELD} value={pwd.current_password} onChange={setP("current_password")} required /></div>
            <div><label className={LABEL}>{t("cdash.newPwd")}</label><input data-testid="pwd-new" type="password" className={FIELD} value={pwd.new_password} onChange={setP("new_password")} required /></div>
          </div>
          <button data-testid="pwd-save-btn" type="submit" disabled={pwdSaving} className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/20 text-white font-semibold text-sm px-6 py-2.5 hover:bg-white/5 active:scale-95 transition-[background-color,transform] duration-300 disabled:opacity-60">
            {pwdSaving ? <Loader2 size={16} className="animate-spin" /> : null} {t("cdash.changePwd")}
          </button>
        </form>

        <div className="rounded-2xl border border-aura-border bg-aura-surface p-6 md:p-8">
          <div className="flex items-center gap-2 mb-3"><Wallet size={18} className="text-zinc-400" /><h2 className="font-display text-lg font-semibold text-white">{t("cdash.nextPayment")}</h2></div>
          <p className="text-sm text-zinc-500">{t("cdash.paymentsNote")}</p>
        </div>
      </div>
    </div>
  );
}
